import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowRight,
  Brain,
  Target,
  Layers,
  Lightbulb,
  Building2,
  ShoppingBag,
  Leaf,
  Landmark,
  Sprout,
  Zap,
  ShieldAlert,
  GraduationCap,
  Network,
  CheckCircle2,
  Clock,
  Play,
  Pause,
  Maximize2,
  X,
  ChevronUp,
  MapPin,
  Phone,
  Mail,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import SolutionPanel from '../components/SolutionPanel';
import CoursesSection from '../components/CoursesSection';
import DemoModal from '../components/DemoModal';
import HeroGlobe from '../components/HeroGlobe';
import { 
  solutions, 
  products, 
  industries, 
  impactMetrics, 
  platformPillars 
} from '../data/mockData';

const iconMap = {
  Brain,
  Target,
  Layers,
  Lightbulb,
  Building2,
  ShoppingBag,
  Leaf,
  Landmark,
  Sprout,
  Zap,
  ShieldAlert,
  GraduationCap,
  Network
};

const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isProductVideoPlaying, setIsProductVideoPlaying] = useState(false);
  const [showProductVideoControl, setShowProductVideoControl] = useState(true);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const modalVideoRef = React.useRef(null);
  const productDemoVideoUrl =
    process.env.REACT_APP_PRODUCT_DEMO_URL ||
    'https://res.cloudinary.com/dvfhiihxr/video/upload/v1771424076/demo-video_tvpzfi.mp4';
  const featuredProduct = products.find((product) => product.status === 'Live');
  const upcomingProducts = products.filter((product) => product.status !== 'Live');
  const productVideoRef = React.useRef(null);
  const productVideoControlTimerRef = React.useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowBackToTop(window.scrollY > 600);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll spy for nav active state
  useEffect(() => {
    const sectionIds = ['solutions', 'products', 'industries', 'courses', 'contact'];
    const observers = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { threshold: 0.15, rootMargin: '-80px 0px -50% 0px' }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleProductVideoToggle = () => {
    const video = productVideoRef.current;
    if (!video) return;

    setShowProductVideoControl(true);

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const revealProductVideoControl = () => {
    if (!isProductVideoPlaying) return;

    setShowProductVideoControl(true);
    if (productVideoControlTimerRef.current) {
      clearTimeout(productVideoControlTimerRef.current);
    }
    productVideoControlTimerRef.current = setTimeout(() => {
      setShowProductVideoControl(false);
    }, 2000);
  };

  useEffect(() => {
    if (productVideoControlTimerRef.current) {
      clearTimeout(productVideoControlTimerRef.current);
      productVideoControlTimerRef.current = null;
    }

    if (isProductVideoPlaying) {
      productVideoControlTimerRef.current = setTimeout(() => {
        setShowProductVideoControl(false);
      }, 2000);
    } else {
      setShowProductVideoControl(true);
    }
  }, [isProductVideoPlaying]);

  useEffect(() => {
    return () => {
      if (productVideoControlTimerRef.current) {
        clearTimeout(productVideoControlTimerRef.current);
      }
      document.body.style.overflow = '';
    };
  }, []);

  const openVideoModal = () => {
    const inlineVideo = productVideoRef.current;
    if (inlineVideo && !inlineVideo.paused) {
      inlineVideo.pause();
    }
    setVideoModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    const modalVideo = modalVideoRef.current;
    if (modalVideo && !modalVideo.paused) {
      modalVideo.pause();
    }
    setVideoModalOpen(false);
    document.body.style.overflow = '';
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && videoModalOpen) closeVideoModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [videoModalOpen]);

  useEffect(() => {
    const video = productVideoRef.current;
    if (!video || videoModalOpen) return undefined;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );

    obs.observe(video);
    return () => obs.disconnect();
  }, [videoModalOpen]);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Skip to content (a11y) */}
      <a href="#solutions" className="skip-to-content">Skip to content</a>

      {/* Sticky Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-slate-950/95 border-b border-slate-800'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img src="/logo.jpeg" alt="Insust Global logo" className="h-10 w-10 object-contain rounded-full" />
              <div>
                <div className="text-2xl font-bold text-white leading-none">Insust Global</div>
                <div className="text-xs text-slate-400">The intelligence of where</div>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-8">
              {[
                { id: 'solutions', label: 'Solutions' },
                { id: 'products', label: 'Products' },
                { id: 'industries', label: 'Industries' },
                { id: 'courses', label: 'Courses' },
                { id: 'contact', label: 'Contact' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`transition-colors font-medium ${
                    activeSection === item.id
                      ? 'text-teal-400'
                      : 'text-slate-300 hover:text-teal-400'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              {/* Desktop Schedule Demo */}
              <Button className="hidden md:inline-flex bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/30" onClick={() => setDemoModalOpen(true)}>
                Schedule Demo
              </Button>

              {/* Mobile Schedule Demo (compact) */}
              <button
                onClick={() => setDemoModalOpen(true)}
                className="md:hidden px-3 py-2 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-md text-sm transition-all duration-300"
              >
                Demo
              </button>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 rounded-md text-slate-200 hover:text-white"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-40 bg-slate-900/95 backdrop-blur-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <img src="/logo.jpeg" alt="Insust Global logo" className="h-10 w-10 object-contain rounded-full" />
                <div>
                  <div className="text-xl font-bold text-white leading-none">Insust Global</div>
                  <div className="text-[11px] text-teal-400 leading-tight">The intelligence of where</div>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-slate-300 hover:text-white transition-colors" aria-label="Close menu">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-4">
              <button onClick={() => { setMobileMenuOpen(false); scrollToSection('solutions'); }} className="text-white text-lg text-left">Solutions</button>
              <button onClick={() => { setMobileMenuOpen(false); scrollToSection('products'); }} className="text-white text-lg text-left">Products</button>
              <button onClick={() => { setMobileMenuOpen(false); scrollToSection('industries'); }} className="text-white text-lg text-left">Industries</button>
              <button onClick={() => { setMobileMenuOpen(false); scrollToSection('courses'); }} className="text-white text-lg text-left">Courses</button>
              <button onClick={() => { setMobileMenuOpen(false); scrollToSection('contact'); }} className="text-white text-lg text-left">Contact</button>
            </nav>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section min-h-screen pt-20 flex items-center justify-start px-6 lg:px-12 relative overflow-hidden">
        {/* Clean background */}
        <div className="absolute inset-0 bg-slate-950"></div>
        <HeroGlobe />

        <div className="max-w-[1100px] relative z-20 text-left pl-6 lg:pl-24">
          <h1 className="text-6xl lg:text-8xl font-bold text-white mb-8 leading-tight tracking-tight">
            The Intelligence<br />of Where
          </h1>
          <p className="text-xl lg:text-2xl text-slate-300 mb-12 max-w-4xl leading-relaxed">
            AI-powered geospatial platforms and solutions that transform spatial data into strategic intelligence for enterprises and infrastructure decisions.
          </p>
          <div className="flex flex-wrap gap-4 justify-start">
            <Button 
              onClick={() => scrollToSection('solutions')}
              size="lg" 
              className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold px-10 py-7 text-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/30"
            >
              Explore Solutions
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
            <Button 
              onClick={() => setDemoModalOpen(true)}
              size="lg" 
              variant="outline" 
              className="border-2 border-slate-600 text-white hover:bg-slate-800 px-10 py-7 text-lg transition-all duration-300"
            >
              Schedule Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section className="py-32 px-6 lg:px-12 bg-slate-900">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              The Insust Platform
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Insust combines AI, GIS, Remote Sensing, Enterprise basemaps, suitability modeling, risk analytics, and decision dashboards into a unified platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platformPillars.map((pillar, index) => {
              const Icon = iconMap[pillar.icon];
              return (
                <div
                  key={index}
                  className="text-center p-8 rounded-2xl bg-slate-800/50 hover:bg-slate-800 transition-all duration-300 border border-slate-700/50 hover:border-teal-500/50"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-teal-500/10 mb-6 border border-teal-500/20">
                    <Icon className="h-8 w-8 text-teal-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-slate-400">
                    {pillar.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions Section - Natural Scroll (No Snap) */}
      <section id="solutions" className="bg-slate-950">
        <div className="py-20 px-6 lg:px-12 text-center">
          <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            Solutions That Transform Data Into Intelligence
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive geospatial solutions designed for enterprise decision-making
          </p>
        </div>
        {solutions.map((solution) => (
          <SolutionPanel key={solution.id} solution={solution} />
        ))}
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-6 lg:px-12 bg-slate-900">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Industry-Specific Products
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Purpose-built platforms that deliver spatial intelligence for specific industries and use cases
            </p>
          </div>

          {featuredProduct && (
            <div className="mb-14 rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-5 md:p-7 lg:p-8">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
                <div className="py-1 lg:py-2">
                <div className="flex items-center justify-between mb-3">
                  <Badge className="bg-teal-500 text-slate-950 font-semibold">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> {featuredProduct.status}
                  </Badge>
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                  {featuredProduct.name}
                </h3>
                <p className="text-sm text-teal-400 font-medium mb-6">
                  {featuredProduct.audience}
                </p>
                <p className="text-slate-300 mb-6 leading-relaxed">
                  {featuredProduct.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {featuredProduct.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                      <div className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-teal-500/10 flex items-center justify-center border border-teal-500/20">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400"></div>
                      </div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-slate-700 flex items-center justify-between flex-wrap gap-4">
                  <p className="text-lg font-bold text-teal-400">
                    {featuredProduct.impact}
                  </p>
                  {featuredProduct.detailPage && (
                    <Link
                      to={`/products/${featuredProduct.id}`}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/30 hover:-translate-y-0.5"
                    >
                      Explore More
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
                </div>

                <div className="overflow-hidden rounded-2xl bg-slate-900/60 border border-slate-800/90 p-3">
                  <div className="flex items-center gap-2 px-2 pb-3">
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-600"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-600"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-600"></span>
                    <span className="text-xs text-slate-400 ml-2">Product Demo</span>
                  </div>
                  <div
                    className="aspect-video bg-slate-950 rounded-xl overflow-hidden relative"
                    onMouseMove={revealProductVideoControl}
                    onTouchStart={revealProductVideoControl}
                  >
                    <video
                      ref={productVideoRef}
                      className="w-full h-full object-contain"
                      preload="metadata"
                      playsInline
                      muted
                      loop
                      disablePictureInPicture
                      controlsList="nodownload noplaybackrate noremoteplayback"
                      onContextMenu={(e) => e.preventDefault()}
                      onPlay={() => setIsProductVideoPlaying(true)}
                      onPause={() => setIsProductVideoPlaying(false)}
                      onEnded={() => setIsProductVideoPlaying(false)}
                    >
                      <source src={productDemoVideoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    <button
                      type="button"
                      onClick={handleProductVideoToggle}
                      className={`absolute inset-0 m-auto w-16 h-16 rounded-full bg-slate-950/70 border border-slate-300/30 text-white flex items-center justify-center transition-all duration-300 hover:bg-slate-950/85 ${
                        showProductVideoControl || !isProductVideoPlaying
                          ? 'opacity-100'
                          : 'opacity-0 pointer-events-none'
                      }`}
                      aria-label={isProductVideoPlaying ? 'Pause product demo video' : 'Play product demo video'}
                    >
                      {isProductVideoPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
                    </button>
                    <button
                      type="button"
                      onClick={openVideoModal}
                      className={`absolute bottom-3 right-3 w-9 h-9 rounded-lg bg-slate-950/70 border border-slate-300/20 text-white flex items-center justify-center transition-all duration-300 hover:bg-slate-950/90 hover:border-teal-400/50 ${
                        showProductVideoControl || !isProductVideoPlaying
                          ? 'opacity-100'
                          : 'opacity-0 pointer-events-none'
                      }`}
                      aria-label="View fullscreen"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-2xl font-bold text-white">Upcoming Products</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcomingProducts.map((product) => (
              <Card key={product.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800 hover:border-teal-500/50 transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                      <Clock className="h-3 w-3 mr-1" /> {product.status}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl text-white mb-2">
                    {product.name}
                  </CardTitle>
                  <p className="text-sm text-teal-400 font-medium">
                    {product.audience}
                  </p>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    {product.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-400">
                        <div className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-teal-500/10 flex items-center justify-center border border-teal-500/20">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-400"></div>
                        </div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-slate-700">
                    <p className="text-lg font-bold text-teal-400">
                      {product.impact}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-32 px-6 lg:px-12 bg-slate-950">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Industries We Serve
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Spatial intelligence tailored to your sector&apos;s unique challenges
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {industries.map((industry) => {
              const Icon = iconMap[industry.icon];
              return (
                <div
                  key={industry.id}
                  className="group relative h-[500px] overflow-hidden rounded-[20px] border border-teal-400/20 transition-all duration-500 ease-out hover:-translate-y-2.5 hover:border-teal-300/80 hover:shadow-[0_20px_60px_rgba(45,212,191,0.3)]"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
                    style={{ backgroundImage: `url('${industry.image}')` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-slate-950/45 to-slate-950/95 transition-all duration-500 group-hover:from-slate-950/25 group-hover:to-slate-950/85" />

                  <div className="relative z-10 flex h-full flex-col justify-end p-8">
                    <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-teal-500/10 border border-teal-500/30 shadow-[0_0_20px_rgba(45,212,191,0.25)]">
                      <Icon className="h-7 w-7 text-teal-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-white leading-tight mb-2">
                      {industry.name}
                    </h3>
                    <p className="text-teal-300 font-medium mb-4">{industry.subtitle}</p>
                    <p className="text-sm text-slate-200/90 leading-relaxed max-h-[260px] overflow-hidden opacity-100 transition-all duration-500 md:max-h-0 md:opacity-0 md:group-hover:max-h-[260px] md:group-hover:opacity-100 md:group-focus-within:max-h-[260px] md:group-focus-within:opacity-100">
                      {industry.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-32 px-6 lg:px-12 bg-slate-900">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 text-center">
            {impactMetrics.map((metric, index) => (
              <div key={index} className="p-8">
                <div className="text-6xl lg:text-7xl font-bold text-teal-400 mb-4">
                  {metric.value}
                </div>
                <div className="text-xl font-bold text-white mb-2">
                  {metric.label}
                </div>
                <div className="text-slate-400">
                  {metric.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <CoursesSection />

      {/* Contact Section */}
      <section id="contact" className="py-32 px-6 lg:px-12 bg-slate-950 border-t border-slate-900/50">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            
            {/* Left Column: Contact Details */}
            <div className="lg:col-span-7 text-left">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">Contact Us</span>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Connect with our GIS & AI Experts
              </h2>
              <p className="text-slate-300 text-base lg:text-lg mb-10 leading-relaxed max-w-xl font-light">
                Whether you have questions about our GIS courses, want to integrate spatial analysis in your workflow, or are interested in our customized property intelligence solutions, our team is ready to connect.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Address Card */}
                <div className="bg-slate-900/40 border border-slate-800/60 p-6 rounded-xl hover:border-teal-500/30 transition-all duration-300 group sm:col-span-2">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0 group-hover:bg-teal-500/20 transition-colors">
                      <MapPin className="h-5 w-5 text-teal-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Registered Office Address</h3>
                      <p className="text-slate-400 text-sm leading-relaxed font-light">
                        RADHANAGAR, GOPINAGAR, Dhaniakhali, Kolkata, West Bengal, India, 712402
                      </p>
                    </div>
                  </div>
                </div>

                {/* Phone Card */}
                <a href="tel:+919434144165" className="bg-slate-900/40 border border-slate-800/60 p-6 rounded-xl hover:border-teal-500/30 transition-all duration-300 group flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0 group-hover:bg-teal-500/20 transition-colors">
                    <Phone className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-0.5">Call Us</h3>
                    <p className="text-teal-400 font-medium text-sm group-hover:underline">
                      +91-9434144165
                    </p>
                  </div>
                </a>

                {/* Email Card */}
                <a href="mailto:insust.global@gmail.com" className="bg-slate-900/40 border border-slate-800/60 p-6 rounded-xl hover:border-teal-500/30 transition-all duration-300 group flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0 group-hover:bg-teal-500/20 transition-colors">
                    <Mail className="h-5 w-5 text-teal-400" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold text-sm mb-0.5">Email Us</h3>
                    <p className="text-teal-400 font-medium text-sm truncate group-hover:underline">
                      insust.global@gmail.com
                    </p>
                  </div>
                </a>

                {/* Website Card */}
                <a href="https://www.insustglobal.com" target="_blank" rel="noopener noreferrer" className="bg-slate-900/40 border border-slate-800/60 p-6 rounded-xl hover:border-teal-500/30 transition-all duration-300 group flex items-center gap-4 sm:col-span-2">
                  <div className="h-10 w-10 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0 group-hover:bg-teal-500/20 transition-colors">
                    <Globe className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-0.5">Official Website</h3>
                    <p className="text-teal-400 font-medium text-sm group-hover:underline">
                      www.insustglobal.com
                    </p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right Column: Demo Call to Action */}
            <div className="lg:col-span-5 bg-slate-900/60 backdrop-blur-md border border-slate-800/80 p-8 lg:p-10 rounded-2xl text-left shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 rounded-full blur-3xl -z-10" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-3xl -z-10" />

              <h3 className="text-2xl font-bold text-white mb-4">Request a Live Platform Demo</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-6 font-light">
                See how INSUST converts geospatial data into actionable decision advantage. During this live 1-on-1 demo, we will walk you through:
              </p>
              <ul className="space-y-3 mb-8 text-sm text-slate-400">
                <li className="flex items-center gap-2 font-light">
                  <CheckCircle2 className="h-4 w-4 text-teal-400 shrink-0" />
                  <span>Interactive map suitability models</span>
                </li>
                <li className="flex items-center gap-2 font-light">
                  <CheckCircle2 className="h-4 w-4 text-teal-400 shrink-0" />
                  <span>Property Intelligence Platform in action</span>
                </li>
                <li className="flex items-center gap-2 font-light">
                  <CheckCircle2 className="h-4 w-4 text-teal-400 shrink-0" />
                  <span>Custom workflows for your industry</span>
                </li>
              </ul>
              <Button 
                size="lg" 
                onClick={() => setDemoModalOpen(true)}
                className="w-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold py-6 text-base transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/20"
              >
                Schedule Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-16 px-6 lg:px-12 border-t border-slate-800">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-5 gap-10 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-3">
                <img src="/logo.jpeg" alt="Insust Global logo" className="h-10 w-10 rounded-full object-contain" />
                <div>
                  <div className="text-2xl font-bold">Insust Global</div>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed max-w-xs mb-6">
                AI-powered geospatial platforms that transform spatial data into strategic intelligence for enterprises and infrastructure decisions.
              </p>
              
              <div className="space-y-3.5 text-sm text-slate-400">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-teal-400 shrink-0 mt-0.5" />
                  <span className="leading-snug max-w-xs font-light">RADHANAGAR, GOPINAGAR, Dhaniakhali, Kolkata, West Bengal, India, 712402</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-teal-400 shrink-0" />
                  <a href="tel:+919434144165" className="hover:text-teal-400 transition-colors font-light">+91-9434144165</a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-teal-400 shrink-0" />
                  <a href="mailto:insust.global@gmail.com" className="hover:text-teal-400 transition-colors font-light">insust.global@gmail.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="h-4 w-4 text-teal-400 shrink-0" />
                  <a href="https://www.insustglobal.com" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors font-light">www.insustglobal.com</a>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-white">Solutions</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/solutions/spatial-foundations" className="hover:text-teal-400 transition-colors">Spatial Foundations</Link></li>
                <li><Link to="/solutions/data-acquisition" className="hover:text-teal-400 transition-colors">Data Acquisition</Link></li>
                <li><Link to="/solutions/site-suitability" className="hover:text-teal-400 transition-colors">Site Suitability</Link></li>
                <li><Link to="/solutions/change-detection" className="hover:text-teal-400 transition-colors">Change Detection</Link></li>
                <li><Link to="/solutions/geoanalytics" className="hover:text-teal-400 transition-colors">GeoAnalytics & BI</Link></li>
                <li><Link to="/solutions/advanced-modeling" className="hover:text-teal-400 transition-colors">Advanced Geospatial Modeling</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-white">Products</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/products/property-intelligence" className="hover:text-teal-400 transition-colors">Property Intelligence</Link></li>
                <li><span className="text-slate-500">GeoMarketing <span className="text-[10px] uppercase tracking-wider text-slate-600">Soon</span></span></li>
                <li><span className="text-slate-500">Logistics Optimizer <span className="text-[10px] uppercase tracking-wider text-slate-600">Soon</span></span></li>
              </ul>
              <h3 className="font-semibold mb-4 mt-6 text-white">Learn</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><button onClick={() => scrollToSection('courses')} className="hover:text-teal-400 transition-colors">GIS Courses</button></li>
                <li><Link to="/solutions/training-support" className="hover:text-teal-400 transition-colors">Training & Support</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-white">Company</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/about" className="hover:text-teal-400 transition-colors">About</Link></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-teal-400 transition-colors">Contact</button></li>
                <li><Link to="/privacy" className="hover:text-teal-400 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-teal-400 transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Insust Global. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Video Fullscreen Modal */}
      {videoModalOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={closeVideoModal}
        >
          <button
            onClick={closeVideoModal}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-slate-800 border border-slate-600 text-white flex items-center justify-center hover:bg-slate-700 transition-colors z-10"
            aria-label="Close video"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                <span className="text-xs text-slate-400 ml-2">Product Demo — Insust Global Property Intelligence Platform</span>
              </div>
              <div className="aspect-video bg-slate-950">
                <video
                  ref={modalVideoRef}
                  className="w-full h-full object-contain"
                  autoPlay
                  controls
                  playsInline
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <source src={productDemoVideoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back to Top */}
      <button
        onClick={scrollToTop}
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        aria-label="Back to top"
      >
        <ChevronUp className="h-5 w-5" />
      </button>

      {/* Demo Modal */}
      <DemoModal open={demoModalOpen} onOpenChange={setDemoModalOpen} />
    </div>
  );
};

export default LandingPage;
